import type Konva from "konva";
import { Arrow } from "konva/lib/shapes/Arrow";
import { Line } from "konva/lib/shapes/Line";
import { Path } from "konva/lib/shapes/Path";
import { Rect } from "konva/lib/shapes/Rect";
import { Text } from "konva/lib/shapes/Text";
import { Transformer } from "konva/lib/shapes/Transformer";
import { Ellipse } from "konva/lib/shapes/Ellipse";
import { Circle } from "konva/lib/shapes/Circle";
import { SpotlightShapeName } from "../stores/telestrationStore";
import { rotatePoint } from "~/telestrations/helpers/spotlightShapeConfig";
import { ShapeType } from "~/telestrations/types/canvas";
import { getScaledPointCoord } from "~/telestrations/helpers/pointCoordinateHelper";
import { BASE_TELESTRATION_STAGE_SIZE } from "~/telestrations/constants/baseTelestrationStageSize";

export function getSVGStringFromKonvaStage(stage: Konva.Stage): string {
  let shapesCount = 0;
  let svgString = `<svg width="${BASE_TELESTRATION_STAGE_SIZE.width}" height="${BASE_TELESTRATION_STAGE_SIZE.height}" xmlns="http://www.w3.org/2000/svg">`;
  const svgContentStartIndex = svgString.length;
  let mask = ``;
  stage.getLayers().forEach((layer) => {
    const children = layer.getChildren();
    shapesCount += children.length;
    children.forEach((shape) => {
      if (shape instanceof Transformer) return;
      let transformStr = `transform="rotate(${shape.attrs.name === SpotlightShapeName.ELLIPSE ? 0 : shape.rotation()}) scale(${shape.scaleX()}, ${shape.scaleY()})"`;
      if (shape instanceof Path) {
        svgString += `<path ${transformStr} id="${shape.id()}" d="${shape.data()}" fill="${shape.fill() || "transparent"}" stroke="${shape.stroke() || "black"}" stroke-width="${shape.strokeWidth()}" />`;
      }
      else if (shape instanceof Ellipse) {
        if (shape.attrs.type === ShapeType.Spotlight) {
          mask += `<ellipse id="${shape.id()}" cx="${shape.x()}" cy="${shape.y()}" rx="${shape.radiusX()}" ry="${shape.radiusY()}" fill="black" ${transformStr} />`;
        }
        else {
          svgString += `<ellipse stroke-dasharray="${shape.dash() || "none"}" ${transformStr} id="${shape.id()}" cx="${shape.x()}" cy="${shape.y()}" rx="${shape.radiusX()}" ry="${shape.radiusY()}" fill="${shape.fill() || "transparent"}" stroke="${shape.stroke() || "black"}" stroke-width="${shape.strokeWidth()}" />`;
        }
      }
      else if (shape instanceof Circle) {
        svgString += `<circle stroke-dasharray="${shape.dash() || "none"}" ${transformStr} id="${shape.id()}" cx="${shape.x() + shape.radius()}" cy="${shape.y() + shape.radius()}" r="${shape.radius()}" fill="${shape.fill() || "transparent"}" stroke="${shape.stroke() || "black"}" stroke-width="${shape.strokeWidth()}" />`;
      }
      else if (shape instanceof Rect) {
        if (shape.attrs.type === ShapeType.Spotlight) {
          const firstPoint = rotatePoint({ x: shape.width() / 2, y: shape.height() / 2 }, shape.rotation());
          const centerX = firstPoint.x + shape.x();
          const centerY = firstPoint.y + shape.y();
          const transformOrigin = shape.rotation() ? `transform-origin="${centerX} ${centerY}"` : "";
          mask += shape.attrs.name === SpotlightShapeName.ELLIPSE
            ? `<circle id="${shape.id()}" cx="${centerX}" cy="${centerY}" r="${Math.min(shape.width(), shape.height()) / 2}" fill="black" ${transformStr} ${transformOrigin} x="${shape.x()}" y="${shape.y()}" width="${shape.width()}" height="${shape.height()}" />`
            : `<rect id="${shape.id()}" x="${shape.x()}" y="${shape.y()}" width="${shape.width()}" height="${shape.height()}" fill="black" ${transformStr} ${transformOrigin} />`;
        }
        else {
          svgString += `<rect stroke-dasharray="${shape.dash() || "none"}" ${transformStr} id="${shape.id()}" x="${shape.x()}" y="${shape.y()}" width="${shape.width()}" height="${shape.height()}" fill="${shape.fill() || "transparent"}" stroke="${shape.stroke() || "black"}" stroke-width="${shape.strokeWidth()}" />`;
        }
      }
      else if (shape instanceof Text) {
        const background = shape.attrs.background;
        const scaleX = shape.scaleX() || 1;
        const scaleY = shape.scaleY() || 1;
        const rotation = shape.rotation() || 0;
        const x = shape.x() / scaleX;
        const y = shape.y() / scaleY;
        const fontSize = shape.fontSize();

        transformStr = `transform="scale(${scaleX}, ${scaleY}) rotate(${rotation}, ${x}, ${y})"`;

        if (background !== undefined && background !== "transparent") {
          const width = shape.width() || 100;
          const height = shape.height() || fontSize * 1.2;
          const approxTextHeight = fontSize * 0.8;
          const totalPadding = height - approxTextHeight;
          const offsetY = totalPadding / 2;

          const backgroundY = y - offsetY;
          const backgroundHeight = height + offsetY;

          svgString += `<rect data-text-background="true" ${transformStr}
            x="${x}" y="${backgroundY}"
            width="${width}" height="${backgroundHeight}"
            fill="${background}" />`;
        }
        svgString += `<text ${transformStr} id="${shape.id()}" x="${x}" y="${y}"
          font-size="${fontSize}"
          font-family="${shape.fontFamily()}"
          fill="${shape.fill() || "black"}"
          dominant-baseline="hanging"
          text-anchor="${shape.align()}">${shape.text()}</text>`;
      }
      else if (shape instanceof Arrow) {
        svgString += konvaArrowToSVG(shape);
      }
      else if (shape instanceof Line) {
        const points = shape.points();
        const lineX = shape.x();
        const lineY = shape.y();
        const scaleX = shape.scaleX();
        const scaleY = shape.scaleY();

        const transformStr = `transform="rotate(${shape.rotation()}, ${lineX}, ${lineY}) scale(${scaleX}, ${scaleY})"`;

        let pointsString = "";
        for (let i = 0; i < points.length; i += 2) {
          pointsString += `${getScaledPointCoord(points[i], scaleX, lineX)},${getScaledPointCoord(points[i + 1], scaleY, lineY)} `;
        }
        svgString += `<${shape.closed() ? "polygon" : "polyline"} id="${shape.id()}" ${transformStr} x="${shape.attrs.startX}" y="${shape.attrs.startY}" points="${pointsString.trim()}" fill="${shape.fill() || "transparent"}" stroke="${shape.stroke() || "black"}" stroke-width="${shape.strokeWidth()}" />`;
      }
    });
  });

  if (shapesCount <= 2) return "";
  if (mask) {
    const maskBg = `<rect skip="true" x="0" y="0" width="${stage.width()}" height="${stage.height()}" fill="#000" mask="url(#spotlight-mask)" opacity=".8"/>`;
    svgString = svgString.substring(0, svgContentStartIndex) + maskBg + svgString.substring(svgContentStartIndex);
    mask = `
    <defs>
      <mask id='spotlight-mask'>
        <rect skip="true" x="0" y="0" width="${stage.width()}" height="${stage.height()}" fill="white" />` + mask + "</mask></defs>";
    svgString += mask;
  }
  svgString += "</svg>";
  return svgString;
}

function konvaArrowToSVG(arrow: Arrow): string {
  const id = arrow.id();
  const points = arrow.points();
  const stroke = arrow.stroke();
  const strokeWidth = arrow.strokeWidth();

  // Points are absolute coordinates relative to stage, not relative to arrow position
  // We need to subtract arrow position to get relative coordinates for SVG path
  const arrowX = arrow.x();
  const arrowY = arrow.y();
  const scaleX = arrow.scaleX();
  const scaleY = arrow.scaleY();
  const rotation = arrow.rotation();

  let svgPath = `M${getScaledPointCoord(points[0], scaleX, arrowX)},${getScaledPointCoord(points[1], scaleY, arrowY)}`;
  for (let i = 2; i < points.length; i += 2) {
    svgPath += ` L${getScaledPointCoord(points[i], scaleX, arrowX)},${getScaledPointCoord(points[i + 1], scaleY, arrowY)}`;
  }

  const transformStr = `transform="rotate(${rotation}, ${arrowX}, ${arrowY}) scale(${scaleX}, ${scaleY})"`;

  // Create unique marker ID for this arrow to handle different colors
  const markerId = `arrowhead-${id}`;

  // Use Konva's default dimensions: pointerLength=10, pointerWidth=10
  // Scale marker with the arrow's stroke width for proportional sizing
  const markerScale = Math.max(0.5, strokeWidth / 3); // Scale factor based on stroke width
  const scaledLength = 10 * markerScale;
  const scaledWidth = 10 * markerScale;

  const svgString = `<defs>
    <marker
      id="${markerId}"
      markerWidth="${scaledLength * 2}"
      markerHeight="${scaledWidth * 2}"
      refX="${scaledLength}"
      refY="${scaledWidth / 2}"
      orient="auto-start-reverse"
      markerUnits="userSpaceOnUse"
      viewBox="-5 -5 ${scaledLength * 2} ${scaledWidth * 2}"
      >
      <path
        skip="true"
        d="M 0 0 L ${scaledLength} ${scaledWidth / 2} L 0 ${scaledWidth} Z"
        fill="none"
        stroke="${stroke}"
        stroke-width="${strokeWidth}"
        stroke-linejoin="miter"
        stroke-linecap="butt" />
    </marker>
  </defs>
  <path stroke-dasharray="${arrow.dash() || "none"}" ${transformStr} id="${id}" stroke="${stroke}" stroke-width="${strokeWidth}" d="${svgPath}" marker-end="url(#${markerId})" fill="none" />`;
  return svgString;
}
